import type { FC } from 'react';
import { useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  Grid,
  styled,
  IconButton,
  Card,
} from '@mui/material';
import { Modal, ModalInner, Label } from '@pflegenavi/web-components';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useTranslation } from 'react-i18next';
import {
  useFormatting,
  useFormatDate,
} from '@pflegenavi/frontend/localization';
import type { EmployeeCardWithDates } from '@pflegenavi/frontend/api-nursing-home';
import {
  isCardTerminated,
  getStatusColor,
  getContextualLimitLabel,
  formatRenewFrequency,
  getEffectiveCashWithdrawalLimits,
  getNextResetDate,
} from '@pflegenavi/frontend/api-nursing-home';

export interface EmployeeCardDetailModalViewProps {
  open: boolean;
  onClose: () => void;
  card: EmployeeCardWithDates;
}

export const EmployeeCardDetailModalView: FC<
  EmployeeCardDetailModalViewProps
> = ({ open, onClose, card }) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();

  const statusColor = useMemo(
    () => getStatusColor(card.card_status),
    [card.card_status]
  );

  const effectiveCashLimits = useMemo(
    () => getEffectiveCashWithdrawalLimits(card),
    [card]
  );

  const nextResetDate = useMemo(
    () => getNextResetDate(card.limit_renew_frequency),
    [card.limit_renew_frequency]
  );

  return (
    <Modal open={open} handleClose={onClose}>
      <ModalInner>
        <ModalCard>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CreditCardIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {t(`myCards.cardType.${card.card_type}`)}
                </Typography>
                <Typography
                  variant="h6"
                  fontFamily="monospace"
                  color="text.secondary"
                >
                  •••• {card.last_four_digits}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Label variant="filled" color={statusColor}>
                {t(`myCards.status.${card.card_status}`)}
              </Label>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Card Info Section */}
          <SectionBox>
            <SectionTitle variant="subtitle1">
              {t('myCards.section.cardInfo')}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.cardType.label')}:</InfoLabel>
                <InfoValue>{t(`myCards.cardType.${card.card_type}`)}</InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.issuedAt')}:</InfoLabel>
                <InfoValue>
                  {fDate(new Date(card.issued_at), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.expiresAt')}:</InfoLabel>
                <InfoValue>
                  {isCardTerminated(card.card_status)
                    ? '--'
                    : fDate(card.expires_at, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        timeZone: 'UTC',
                      })}
                </InfoValue>
              </Grid>
              {card.terminated_at && (
                <Grid item xs={6}>
                  <InfoLabel>{t('myCards.terminatedAt')}:</InfoLabel>
                  <InfoValue>
                    {fDate(new Date(card.terminated_at), {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </InfoValue>
                </Grid>
              )}
            </Grid>
          </SectionBox>

          <Divider sx={{ my: 3 }} />

          {/* Financial Limits Section */}
          <SectionBox>
            <SectionTitle variant="subtitle1">
              {t('myCards.section.financialLimits')}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.transactionLimit')}:</InfoLabel>
                <InfoValue>{fCurrencyCents(card.transaction_limit)}</InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>
                  {t(getContextualLimitLabel(card.limit_renew_frequency))}:
                </InfoLabel>
                <InfoValue>{fCurrencyCents(card.total_limit)}</InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.spendingBalance')}:</InfoLabel>
                <InfoValue>{fCurrencyCents(card.spending_balance)}</InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.availableBalance')}:</InfoLabel>
                <InfoValue fontWeight={700} color="success.main">
                  {fCurrencyCents(card.available_balance)}
                </InfoValue>
              </Grid>
            </Grid>
          </SectionBox>

          {/* Limit Renewal Section */}
          {card.limit_renew_frequency && (
            <>
              <Divider sx={{ my: 3 }} />
              <SectionBox>
                <SectionTitle variant="subtitle1">
                  {t('myCards.section.limitRenewal')}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoLabel>{t('myCards.renewFrequency')}:</InfoLabel>
                    <InfoValue>
                      {t(formatRenewFrequency(card.limit_renew_frequency))}
                    </InfoValue>
                  </Grid>
                  {nextResetDate && (
                    <Grid item xs={6}>
                      <InfoLabel>{t('myCards.nextRenewal')}:</InfoLabel>
                      <InfoValue>
                        {isCardTerminated(card.card_status)
                          ? '--'
                          : fDate(nextResetDate, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                      </InfoValue>
                    </Grid>
                  )}
                </Grid>
              </SectionBox>
            </>
          )}

          {/* Cash Withdrawal Restrictions Section */}
          <Divider sx={{ my: 3 }} />
          <SectionBox>
            <SectionTitle variant="subtitle1">
              {t('myCards.section.cashWithdrawal')}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.maxPerTransaction')}:</InfoLabel>
                <InfoValue>
                  {fCurrencyCents(effectiveCashLimits.max_per_transaction)}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.minAmount')}:</InfoLabel>
                <InfoValue>
                  {fCurrencyCents(effectiveCashLimits.min_amount)}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.dailyLimit')}:</InfoLabel>
                <InfoValue>
                  {fCurrencyCents(effectiveCashLimits.daily_limit)}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.fourDayLimit')}:</InfoLabel>
                <InfoValue>
                  {fCurrencyCents(effectiveCashLimits.four_day_limit)}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.dailyFrequency')}:</InfoLabel>
                <InfoValue>
                  {effectiveCashLimits.daily_frequency}{' '}
                  {t('myCards.transactions')}
                </InfoValue>
              </Grid>
              <Grid item xs={6}>
                <InfoLabel>{t('myCards.fourDayFrequency')}:</InfoLabel>
                <InfoValue>
                  {effectiveCashLimits.four_day_frequency}{' '}
                  {t('myCards.transactions')}
                </InfoValue>
              </Grid>
            </Grid>
          </SectionBox>
        </ModalCard>
      </ModalInner>
    </Modal>
  );
};

const ModalCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    minWidth: { xs: '100%', sm: 500, md: 600 },
    maxHeight: '90vh',
    overflow: 'auto',
  })
);

const SectionBox = styled(Box)({
  marginBottom: 16,
});

const SectionTitle = styled(Typography)(({ theme }) =>
  theme.unstable_sx({
    fontWeight: 600,
    mb: 2,
    color: 'text.primary',
  })
);

const InfoLabel = styled(Typography)(({ theme }) =>
  theme.unstable_sx({
    color: 'text.secondary',
    fontSize: '0.875rem',
    mb: 0.5,
  })
);

const InfoValue = styled(Typography)<{ fontWeight?: number }>(
  ({ theme, fontWeight }) =>
    theme.unstable_sx({
      fontSize: '1rem',
      fontWeight: fontWeight || 500,
    })
);
